export interface CompanyInformationAddress {
  readonly postadress?: string;
  readonly postnummer?: string;
  readonly postort?: string;
}

export interface CompanyInformationResponse {
  readonly adress: CompanyInformationAddress;
  readonly namn?: string;
}

export interface CompleteCompanyInformationAddress {
  readonly postadress: string;
  readonly postnummer: string;
  readonly postort: string;
}

const POST_OFFICE_BOX_PATTERN =
  /\b(?:(?:p\.?\s*o\.?|post)\s*box|box|postfack)\b/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getOptionalString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === "string" ? value : undefined;
}

export function hasStreetNumber(address: string): boolean {
  return !POST_OFFICE_BOX_PATTERN.test(address.trim()) && /\d/.test(address);
}

function normalizePythonLiteralResponse(rawResponse: string): string {
  return rawResponse
    .replace(/\bNone\b/g, "null")
    .replace(/\bFalse\b/g, "false")
    .replace(/\bTrue\b/g, "true")
    .replace(/'((?:\\'|[^'])*)'/g, (_, value: string) =>
      JSON.stringify(value.replace(/\\'/g, "'")),
    );
}

function parseUnknownResponse(rawResponse: string): unknown {
  try {
    return JSON.parse(rawResponse);
  } catch {
    return JSON.parse(normalizePythonLiteralResponse(rawResponse));
  }
}

/** Parses the company information lookup response into typed form data. */
export function parseCompanyInformationResponse(
  rawResponse: unknown,
): CompanyInformationResponse | null {
  try {
    const parsedResponse =
      typeof rawResponse === "string"
        ? parseUnknownResponse(rawResponse)
        : rawResponse;

    if (!isRecord(parsedResponse) || !isRecord(parsedResponse.adress)) {
      return null;
    }

    return {
      adress: {
        postadress: getOptionalString(parsedResponse.adress, "postadress"),
        postnummer: getOptionalString(parsedResponse.adress, "postnummer"),
        postort: getOptionalString(parsedResponse.adress, "postort"),
      },
      namn: getOptionalString(parsedResponse, "namn"),
    };
  } catch {
    return null;
  }
}

/** Checks whether the lookup response includes all address fields required by the form. */
export function hasCompleteAddress(
  address: CompanyInformationAddress,
): address is CompleteCompanyInformationAddress {
  return (
    address.postadress !== undefined &&
    address.postnummer !== undefined &&
    address.postort !== undefined
  );
}

/** Formats a city name from the lookup response for display in the form. */
export function formatCityName(cityName: string): string {
  return cityName.charAt(0).toUpperCase() + cityName.toLowerCase().slice(1);
}
